import type { AlarmRoutine, RoutineSuggestion, WeeklyRoutineWindow } from "@src/services/api/types";
import { formatTime12 } from "@src/utils/format-date";
import { collapseRoutineWindows } from "@src/features/schedules/utils/schedule-utils";

type TimeRangeItem = WeeklyRoutineWindow["timeRanges"][number];

export function isFullDayTimeRange(range: TimeRangeItem): boolean {
  if (range.kind !== "time_range") return false;
  return range.start.hour === 0 && range.start.minute === 0 && range.end.hour === 24 && range.end.minute === 0;
}

/**
 * Formats an alarm routine for display as an array of strings.
 * Each string represents one line of the formatted routine.
 */
export function formatRoutineForDisplay(
  routine: AlarmRoutine,
  showNumericMidnight: boolean = false,
): { name: string; timeRanges: string }[] {
  if (routine.kind === "weekly") {
    if (routine.windows.length === 0) {
      return [{ name: "Never armed", timeRanges: "" }];
    }

    // Collapse weekdays and weekends before formatting
    const collapsedWindows = collapseRoutineWindows(routine.windows);

    return collapsedWindows.map((window) => {
      const days = window.days.join(", ");
      const timeRanges = window.timeRanges
        .map((range: TimeRangeItem) => {
          if (range.kind === "all_day" || isFullDayTimeRange(range)) {
            return "All day";
          }
          const startLabel = formatTime12(range.start.hour, range.start.minute, { showNumericMidnight });
          const endLabel = formatTime12(range.end.hour, range.end.minute, { showNumericMidnight });
          return `${startLabel} - ${endLabel}`;
        })
        .join(", ");
      // return `${window.name || days}: ${timeRanges}`;
      return {
        name: window.name || days,
        timeRanges: timeRanges,
      };
    });
  } else if (routine.kind === "rotating" && routine.start) {
    const start = routine.start;
    const date = new Date(start.year, start.month - 1, start.day);
    const formattedDate = new Intl.DateTimeFormat("en-CA", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date);
    return [{ name: `Rotating pattern starting ${formattedDate}`, timeRanges: "" }];
  }

  return [{ name: "Schedule configured", timeRanges: "" }];
}

/**
 * Derives a human-readable label for a suggestion in the "change routine" UI.
 * Prefers the server-provided name, then a non-"All day" time range, then the
 * day label of the first formatted line. The final string fallback prevents
 * an empty label when no specific time range or day grouping is available.
 */
export function deriveSuggestionLabel(suggestion: RoutineSuggestion): string {
  if (suggestion.name) return suggestion.name;
  const lines = formatRoutineForDisplay(suggestion.routine);
  const timeRange = lines.find((line) => line.timeRanges && line.timeRanges !== "All day")?.timeRanges;
  if (timeRange) return timeRange;
  return lines[0]?.name ?? "Schedule";
}

export function getSuggestionAction(suggestion: RoutineSuggestion): "armed" | "disarmed" {
  const isDisarmed = suggestion.routine.kind === "weekly" && suggestion.routine.windows.length === 0;
  return isDisarmed ? "disarmed" : "armed";
}
